import { makeStyles } from '@material-ui/core'
import { AlertProps as Mui5AlertProps, default as Mui5Alert } from '@mui/material/Alert'
import classnames from 'classnames'
import { forwardRef, isValidElement, MouseEventHandler } from 'react'
import { useComponentVersion } from '../hooks'
import { AlertContent } from '../other/alert/AlertContent'
import { AlertIcon } from '../other/alert/AlertIcon'

export enum OsAlertVersion {
  V1 = 1, // OS UI 2.0
  V2 = 2, // MUI5 + Stratis
}
export type Severities = Mui5AlertProps['severity']
export type Variants = Mui5AlertProps['variant']
export type AlertProps = Omit<Mui5AlertProps, 'icon'> & {
  severity?: Severities
  primaryButtonLabel?: string
  secondaryButtonLabel?: string
  onClickPrimaryButton?: MouseEventHandler
  onClickSecondaryButton?: MouseEventHandler
  onPressCloseIcon?: MouseEventHandler
  fixedWidth?: boolean
}

// * MUI Alert API Spec  https://mui.com/material-ui/react-alert/
export const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  let {
    severity = 'info',
    variant = 'standard',
    onPressCloseIcon,
    primaryButtonLabel = 'Primary',
    secondaryButtonLabel = 'Secondary',
    onClickPrimaryButton,
    onClickSecondaryButton,
    children,
    fixedWidth, // The old Alert component did not have a fixed width. Adding a fixed width now could break some designs, so a prop was added to make it optional
    ...rest
  } = props

  let version = useComponentVersion('alert')

  switch (version) {
    case OsAlertVersion.V2:
      return (
        <Mui5Alert
          ref={ref}
          variant={variant}
          icon={false}
          severity={severity}
          sx={{ width: !fixedWidth ? 'auto' : 384 }} // Added fixed width 384px for new design
          {...rest}
        >
          {isValidElement(children) ? (
            children
          ) : (
            <AlertContent
              severity={severity}
              variant={variant}
              primaryButtonLabel={primaryButtonLabel}
              secondaryButtonLabel={secondaryButtonLabel}
              onClickPrimaryButton={onClickPrimaryButton}
              onClickSecondaryButton={onClickSecondaryButton}
              onPressCloseIcon={onPressCloseIcon}
              children={children}
            />
          )}
        </Mui5Alert>
      )

    default:
      return (
        <V1Alert ref={ref} icon={<AlertIcon severity={severity} />} severity={severity} {...rest}>
          {children}
        </V1Alert>
      )
  }
})

const v1Styles = makeStyles<any, { severity: Severities }>((theme) => ({
  wrapper: ({ severity }) => ({
    display: 'flex',
    background: theme['alertBg_' + severity],
    color: theme['alert_' + severity],
    borderRadius: 4,
    padding: 16,
    alignItems: 'center',
  }),
  content: {
    paddingTop: 3,
    width: '100%',
  },
}))

const V1Alert = forwardRef<HTMLDivElement, Mui5AlertProps>((props, ref) => {
  const { severity, children, icon, className, ...rest } = props
  const classes = v1Styles({ severity })

  return (
    <div className={classnames(classes.wrapper, className)} ref={ref} {...rest}>
      {icon}
      <div className={classes.content}>{children}</div>
    </div>
  )
})

declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    outlined: false
  }
}
